import React, { useCallback } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import styled from 'styled-components';
import { addDays, compareAsc } from 'date-fns';
import ru from 'date-fns/locale/ru';

import { datepickerName } from '../../constants';
import { MAXIMUM_NIGHTS, MAXIMUM_SHOW_DAYS, MONTH_COUNT, MONTH_COUNT_MOBILE } from './constants';
import renderDayContents from './components/renderDayContens';
import renderCustomHeader from './components/renderCustomHeader';
import type { CalendarProps } from './types';

registerLocale('ru', ru);

export const Calendar = ({
  open,
  startDate,
  endDate,
  onChange,
  minDate,
  excludeDates,
  isEndDateFocus,
  isMobile = false,
  isWizard = false,
  searchCalendarDuration,
  indicationMap,
  setStartCurrentMonth,
  isPreset = false,
}: CalendarProps): JSX.Element | null => {
  const calcMaxDate = useCallback(() => {
    const maximumShowDays = addDays(new Date(), MAXIMUM_SHOW_DAYS);
    if (startDate && isEndDateFocus) {
      const maximumNights = addDays(startDate, MAXIMUM_NIGHTS);
      if (compareAsc(new Date(maximumNights), new Date(maximumShowDays)) < 0) {
        return maximumNights;
      }
    }
    return maximumShowDays;
  }, [startDate, isEndDateFocus]);

  if (!open) return null;

  return (
    <StyledWrapper $isMobile={isMobile} $isWizard={isWizard} $isPreset={isPreset}>
      <StyledReactDatePicker
        locale="ru"
        startDate={startDate}
        endDate={endDate}
        minDate={minDate}
        showPopperArrow={false}
        onChange={onChange}
        selectsEnd={isEndDateFocus}
        renderDayContents={renderDayContents({
          startDate,
          endDate,
          isMobile,
          excludeDates,
          searchCalendarDuration,
          indicationMap,
          isPreset,
        })}
        openToDate={startDate || minDate}
        disabledKeyboardNavigation
        maxDate={calcMaxDate()}
        // нет в @types/react-datepicker
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        /* @ts-ignore */
        selectsDisabledDaysInRange
        inline
        monthsShown={isMobile ? MONTH_COUNT_MOBILE : MONTH_COUNT}
        renderCustomHeader={renderCustomHeader({
          isMobile,
          setStartCurrentMonth,
        })}
      />
    </StyledWrapper>
  );
};

const StyledReactDatePicker = styled(ReactDatePicker)`
  width: 100%;
`;

const StyledWrapper = styled.div<{
  $isMobile: boolean;
  $isWizard?: boolean;
  $isPreset?: boolean;
}>`
  height: 100%;

  .${datepickerName} {
    font-family: 'Backpack', Helvetica, sans-serif;
    border: none;
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    background-color: #fff;

    ${({ $isMobile, $isWizard }) =>
      $isMobile &&
      !$isWizard &&
      `
        z-index: 120;
      `}

    // desktop position
    ${({ $isMobile }) =>
      !$isMobile &&
      `
        display: inline-block;
        width: 680px;
        z-index: 999;
      `};

    &__header {
      background-color: #fff;
      border: none;
      padding: 0;
    }

    &__current-month {
      font-size: 18px;
      line-height: 22px;
      color: #17181a;
      text-transform: capitalize;
      border: none;
      margin-bottom: 16px;
    }

    &__day-name {
      color: #c2c5cc;

      &:nth-last-child(-n + 2) {
        color: #efc1c7;
      }

      &s {
        text-transform: uppercase;
        display: flex;
        justify-content: space-around;
        padding: 0 16px;
      }
    }

    &__month {
      padding: 8px 16px 16px;
      margin: 0;
    }

    &__week {
      display: flex;
    }

    &__day {
      width: ${({ $isMobile }) => `${!$isMobile ? '44px' : 'calc((100vw - 32px) / 7)'}`};
      height: ${({ $isMobile }) => `${!$isMobile ? '44px' : 'calc((100vw - 32px) / 7)'}`};
      margin: 0;
      border-radius: 0;
      font-size: 18px;
      line-height: 22px;
      color: #4d4d4d;

      &--disabled {
        color: #c2c5cc;
      }

      ${({ $isPreset }) =>
        $isPreset &&
        `
          border-top: 1px solid #fff;
          border-bottom: 1px solid #fff;

          &:hover {
            background-color: #f3f2e8 !important;
          }

          &--in-range {
            background-color: #e6f2ff !important;
            color: #17181a;
            font-weight: bold;
          }

          &--in-selecting-range {
            background-color: #fff;
          }
      `}

      ${({ $isPreset }) =>
        !$isPreset &&
        `
          &--in-range {
            background-color: #f3f2e8;
            color: #4d4d4d;

            &:hover {
              background-color: #f3f2e8;
            }
          }

          &--in-selecting-range {
            background-color: #e6f2ff;

            &:not(.${datepickerName}__day--in-range) {
              background-color: #e6f2ff;
            }
          }

          &--range-start,
          &--range-end {
            color: #17181a;
            font-weight: bold;
            position: relative;

            &:before {
              position: absolute;
              left: 0;
              top: -12px;
              font-size: 9px;
              line-height: 12px;
              font-weight: normal;
              padding: 0 4px;
              border-radius: 4px 4px 0 0;
            }
          }

          &--range-start {
            background-color: #fffbe5;

            &:before {
              content: 'Туда';
              color: #17181a;
              background-color: #f8e71c;
            }
          }

          &--range-end {
            background-color: #cce763;
            border-color: #6abf40;

            &:before {
              content: 'Обратно';
              color: #fff;
              background-color: #6abf40;
            }
          }
        `}

      &.${datepickerName}__day--outside-month {
        * {
          display: none;
        }
        background-color: transparent !important;
        border: none;
        color: transparent;

        &:before {
          content: none;
        }
      }
    }
  }

  ${({ $isMobile, $isWizard }) =>
    $isMobile &&
    `
      ${!$isWizard && `width: 100%;`}
      display: flex;
      justify-content: center;
      overflow-y: scroll;
    `}
`;
